import { useState } from 'react';
import type { Pagina } from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import { Cargando, Chip, Encabezado, Metrica, Panel, Vacio } from '../componentes/ui';
import { useRecurso } from '../lib/consultas';
import { haceCuanto, useQueSeSabeDeLosAparatos } from '../lib/enlace';

interface Dispositivo {
  id: string;
  nombre: string;
  tipo: string;
  vivo: boolean;
  puntoAcceso: { nombre: string };
  carril: { nombre: string } | null;
}

/**
 * Los totales salen de `total` en la paginación pidiendo una sola fila, en vez
 * de un endpoint de estadísticas. Con un padrón de cientos de unidades el
 * conteo es instantáneo, y evita una superficie de API que habría que mantener
 * en paralelo.
 */
function MetricaRemota({
  etiqueta,
  ruta,
  icono,
  tonoIcono,
}: {
  etiqueta: string;
  ruta: string;
  icono: (p: { tam?: number }) => React.ReactNode;
  tonoIcono?: string;
}) {
  const { datos, cargando } = useRecurso<Pagina<unknown>>(`${ruta}?porPagina=1`);
  return (
    <Metrica
      etiqueta={etiqueta}
      valor={cargando ? '—' : (datos?.total ?? 0)}
      icono={icono}
      tonoIcono={tonoIcono}
    />
  );
}

/**
 * Un grupo de aparatos que se abre al tocarlo.
 *
 * **El resumen se queda a la vista y lo que se pliega son los nombres.** Once
 * renglones idénticos, casi todos en verde, obligan a leerlos uno por uno para
 * encontrar el que importa; y esta tarjeta existe para lo contrario. Con el
 * conteo en la cabecera, «Sin latido · 3» se ve de un vistazo desde el otro
 * lado de la caseta, y quién es exactamente está a un toque.
 *
 * Un grupo vacío no se pinta: «Sin latido · 0» ocupa sitio para no decir nada.
 */
function Grupo({
  titulo,
  tono,
  aparatos,
  sabeLatidos,
}: {
  titulo: string;
  tono: 'ok' | 'malo' | 'neutro';
  aparatos: Dispositivo[];
  sabeLatidos: boolean;
}) {
  // Los caídos abiertos de entrada: es lo que alguien vino a ver. Lo que está
  // bien se resume en su número y no hace falta abrirlo casi nunca.
  const [abierto, setAbierto] = useState(tono === 'malo');
  if (aparatos.length === 0) return null;

  // **La fila entera en rojo, no solo la etiqueta.** Un chip de color al final
  // de un renglón se pierde entre los demás; el renglón teñido se ve desde el
  // otro lado de la caseta sin buscarlo. Y solo aparece cuando hay algo caído
  // —un grupo vacío ni se pinta— así que el rojo no se convierte en parte del
  // paisaje: cuando está, es porque hay que atenderlo.
  const alarma = tono === 'malo';

  return (
    <div className="border-b border-outline-variant last:border-b-0">
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        aria-expanded={abierto}
        className={`toque flex w-full items-center gap-3 px-6 py-4 text-left transition-colors ${
          alarma
            ? 'bg-error-container text-error hover:bg-error-container/80'
            : 'hover:bg-surface-container-high'
        }`}
      >
        <Iconos.siguiente
          tam={20}
          className={`shrink-0 transition-transform ${alarma ? 'text-error' : 'text-on-surface-variant'} ${abierto ? 'rotate-90' : ''}`}
        />
        <span className={`flex-1 text-body-md ${alarma ? 'text-error' : 'text-on-surface'}`}>
          {titulo}
        </span>
        <Chip tono={tono}>{aparatos.length}</Chip>
      </button>

      {abierto && (
        <ul
          className={`divide-y divide-outline-variant border-t border-outline-variant ${
            alarma ? 'border-l-4 border-l-error bg-error-container/20' : ''
          }`}
        >
          {aparatos.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-3 pl-14 pr-6">
              {/* Un punto por fila. El grupo ya dice de qué color va, pero
                  leyendo una lista larga se pierde la cabecera de vista: el
                  punto vuelve a decirlo en cada renglón, que es donde está el
                  ojo. */}
              <span
                aria-hidden
                className={`size-2.5 shrink-0 rounded-full ${
                  tono === 'ok' ? 'bg-success' : tono === 'malo' ? 'bg-error' : 'bg-outline'
                }`}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body-md text-on-surface">{d.nombre}</p>
                <p className="truncate text-label-sm text-on-surface-variant">
                  {d.puntoAcceso.nombre}
                  {d.carril ? ` · ${d.carril.nombre}` : ''} ·{' '}
                  {d.tipo.replace(/_/g, ' ').toLowerCase()}
                </p>
              </div>
              {!sabeLatidos && <Chip tono="neutro">Sin dato aquí</Chip>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function PanelInicio() {
  const dispositivos = useRecurso<Dispositivo[]>('/topologia/dispositivos');
  const lista = dispositivos.datos ?? [];
  const vivos = lista.filter((d) => d.vivo).length;
  const todosVivos = lista.length > 0 && vivos === lista.length;
  const enLinea = lista.filter((d) => d.vivo);
  const caidos = lista.filter((d) => !d.vivo);

  const { sabeLatidos, ultimoReporteEn, esNube } = useQueSeSabeDeLosAparatos();

  return (
    <>
      <Encabezado titulo="Panel" descripcion="Estado del padrón y de los aparatos" />

      <div className="grid grid-cols-1 gap-gutter md:grid-cols-2 xl:grid-cols-4">
        <MetricaRemota etiqueta="Unidades" ruta="/padron/unidades" icono={Iconos.unidades} />
        <MetricaRemota etiqueta="Residentes" ruta="/padron/residentes" icono={Iconos.personas} />
        <MetricaRemota
          etiqueta="Vehículos"
          ruta="/padron/vehiculos"
          icono={Iconos.vehiculo}
          tonoIcono="text-tertiary bg-tertiary-container/20"
        />
        <Metrica
          etiqueta={sabeLatidos ? 'Aparatos en línea' : 'Aparatos dados de alta'}
          valor={
            dispositivos.cargando
              ? '—'
              : sabeLatidos
                ? `${vivos}/${lista.length}`
                : `${lista.length}`
          }
          nota={
            !sabeLatidos
              ? (haceCuanto(ultimoReporteEn) ?? 'La caseta no ha reportado')
              : todosVivos
                ? 'Óptimo'
                : lista.length
                  ? 'Revisar'
                  : undefined
          }
          icono={Iconos.aparatos}
          tonoIcono={
            !sabeLatidos
              ? 'text-on-surface-variant bg-surface-container-high'
              : todosVivos
                ? 'text-success bg-success-container/40'
                : 'text-error bg-error-container'
          }
        />
      </div>

      <section className="mt-stack-lg">
        <Panel>
          <div className="border-b border-outline-variant px-6 py-5">
            <h2 className="text-headline-md text-on-surface">Aparatos</h2>
            {!sabeLatidos && (
              <p className="mt-1 text-body-md text-on-surface-variant">
                {esNube && ultimoReporteEn
                  ? `Su caseta no reporta desde ${haceCuanto(ultimoReporteEn)}, así que lo que se ve de los aparatos ya no vale. Puede ser que el enlace esté caído, no ellos.`
                  : 'Esta caseta todavía no reporta a la nube, así que desde aquí se ve qué hay dado de alta, no si está encendido.'}
              </p>
            )}
          </div>

          {dispositivos.cargando && <Cargando />}
          {!dispositivos.cargando && lista.length === 0 && (
            <Vacio>Todavía no hay aparatos dados de alta.</Vacio>
          )}

          {!dispositivos.cargando && lista.length > 0 && !sabeLatidos && (
            <Grupo titulo="Dados de alta" tono="neutro" aparatos={lista} sabeLatidos={false} />
          )}

          {!dispositivos.cargando && sabeLatidos && (
            <>
              {/* Los caídos primero: es lo que hay que atender, y es lo único
                  que justifica que esta tarjeta exista. */}
              <Grupo titulo="Sin latido" tono="malo" aparatos={caidos} sabeLatidos />
              <Grupo titulo="En línea" tono="ok" aparatos={enLinea} sabeLatidos />
            </>
          )}
        </Panel>
      </section>
    </>
  );
}
